/**
 * /login/code — the iOS-PWA-friendly login path: exchange a short-lived
 * pairing code (minted from an already-logged-in browser, see
 * src/pages/Settings.tsx's "Connecter un autre appareil") for a JWT, via
 * the public `POST /api/pairing/consume`. An installed iOS PWA can't
 * receive a magic-link email (it opens in Safari, not here), so this is
 * the only way to sign in without leaving the app.
 *
 * On success, stores the JWT exactly like src/pages/AuthCallback.tsx does.
 */
import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import {
  Container, PageLayout, Card, Input, Button, ErrorBanner, MutedText, Label, IconButton,
} from "../components/ui";
import { Logo } from "../components/Logo";
import { pairingConsume } from "../lib/dataSource";
import { useAuth } from "../lib/auth";
import { isPairingCodeComplete, normalizePairingCode } from "../lib/pairingCode";

type State = "idle" | "loading" | "error";

export default function LoginCode() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { refreshMe, setAuthenticated } = useAuth();
  const [code, setCode] = useState("");
  const [state, setState] = useState<State>("idle");

  async function submit(e: FormEvent) {
    e.preventDefault();
    setState("loading");
    try {
      const { token } = await pairingConsume(normalizePairingCode(code));
      localStorage.setItem("jwt", token);
      setAuthenticated();
      await refreshMe();
      navigate("/", { replace: true });
    } catch {
      setState("error");
    }
  }

  return (
    <PageLayout>
      <Container className="flex min-h-screen flex-col justify-center gap-6 py-10">
        <div className="text-center relative">
          <IconButton
            onClick={() => navigate("/login")}
            aria-label={t("common.back")}
            className="absolute left-0 top-0"
          >
            <ArrowLeft size={18} />
          </IconButton>
          <Logo size={56} className="mb-3" />
          <h1 className="headline text-3xl">{t("loginCode.title")}</h1>
          <MutedText className="mt-2">{t("loginCode.subtitle")}</MutedText>
        </div>
        <Card>
          <form onSubmit={submit} className="flex flex-col gap-3">
            <div>
              <Label>{t("loginCode.codeLabel")}</Label>
              <Input
                type="text"
                required
                autoComplete="one-time-code"
                autoCapitalize="characters"
                autoCorrect="off"
                spellCheck={false}
                inputMode="text"
                placeholder="ABCD-EFGH"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                className="text-center text-xl font-heading tracking-[0.2em] uppercase"
              />
            </div>
            {state === "error" && <ErrorBanner>{t("loginCode.error")}</ErrorBanner>}
            <Button
              type="submit"
              disabled={state === "loading" || !isPairingCodeComplete(code)}
              className="w-full"
            >
              {state === "loading" ? t("loginCode.checking") : t("loginCode.submit")}
            </Button>
          </form>
        </Card>
        <p className="text-center text-violet-soft text-sm font-body px-4">
          {t("loginCode.hint")}
        </p>
      </Container>
    </PageLayout>
  );
}
